import React, { useEffect, useMemo, useState } from 'react';

import { Button, Drawer, Progress, Tabs, Typography } from 'antd';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import FormSelect from '@src/components/Form/components/FormSelect';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import type { ReportScheduleType } from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ScheduleTriggersTable from '@src/packages/events/components/ScheduleTriggersTable';
import TimelineEvent from '@src/packages/events/components/TimelineEvent';
import { getFullUrl } from '@src/packages/utils';
import useGetParams from '@src/packages/utils/hooks/useGetParams';
import IntlMessages from '@src/util/IntlMessages';
import { displayProgressPercentage } from '@src/packages/utils/functions/triggers/progress';

import ReportEventErrorState from './ReportEventErrorState';
import { getReportEventRoute } from './reportEventRoute';
import styles from './styles.module.less';
import { useReportEventDrilldown } from './useReportEventDrilldown';

const { Text } = Typography;

interface Props {
  isVisible: boolean;
  onCancel: () => void;
  organizationId: string;
  divisionIds: string[];
  scheduleType: ReportScheduleType;
}

/** Detail drawer shared by Tasks and Schedules; checkpoint routes can use the same contract. */
const ReportEventDrawer: React.FC<Props> = ({
  isVisible,
  onCancel,
  organizationId,
  divisionIds,
  scheduleType,
}) => {
  const q = useGetParams();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { orgId } = useParams<{ orgId: string }>();
  const [activeTab, setActiveTab] = useState('events');
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const scheduleId = q.get('scheduleId') ?? '';
  const triggerIdParam = q.get('triggerId');
  const triggerId = triggerIdParam && triggerIdParam !== 'null' ? triggerIdParam : undefined;
  const triggerRefId = q.get('triggerRefId') ?? undefined;
  const divId = q.get('divId') ?? '';
  const interval = q.get('groupBy')?.replace('/events', '') ?? 'day';
  const bucketStart = Number(q.get('dateKey'));
  const calendarTimeZone = q.get('calendarTimeZone');
  const gte = Number(q.get('gte'));
  const lte = Number(q.get('lte'));
  const validInterval = ['day', 'week', 'month', 'quarter', 'year'].includes(interval);
  useEffect(() => {
    if (isVisible && !triggerId) {
      setActiveTab('events');
    }
  }, [isVisible, triggerId]);
  const filters =
    isVisible &&
    organizationId &&
    scheduleId &&
    validInterval &&
    Number.isFinite(bucketStart) &&
    Number.isFinite(gte) &&
    Number.isFinite(lte)
      ? {
          organizationId,
          divisionIds,
          scheduleId,
          scheduleType,
          range: { gte, lte },
          bucketStart,
          ...(calendarTimeZone ? { calendarMode: true } : {}),
          interval: interval as 'day' | 'week' | 'month' | 'quarter' | 'year',
          eventTypeIds: selectedEventTypes,
          ...(triggerId ? { triggerId } : {}),
          ...(triggerRefId ? { triggerRefId } : {}),
        }
      : null;
  const { data, loading, loadingMore, error, errorCode, loadMore, refresh } = useReportEventDrilldown(filters);
  useEffect(() => {
    if (!isVisible) return;
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [isVisible, refresh]);
  const eventTypes = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; icon?: string; color?: unknown }>();
    for (const trigger of data?.triggerSummary ?? []) {
      const type = trigger.eventType;
      if (type?.id)
        byId.set(type.id, {
          id: type.id,
          name: type.name ?? '',
          icon: type.icon,
          color: type.color,
        });
    }
    return Array.from(byId.values());
  }, [data?.triggerSummary]);
  const total = data?.completion.expectedExecutions ?? 0;
  const finished = data?.completion.finishedExecutions ?? 0;
  const openTrigger = (trigger: {
    id?: string;
    triggerId?: string;
    finishedExecutions?: number;
    expectedExecutions?: number;
  }) => {
    if (!trigger.id) return;
    const path = getFullUrl(
      getReportEventRoute(
        scheduleType,
        orgId,
        divId,
        trigger.id,
        scheduleId,
        gte,
        lte,
        bucketStart,
        interval,
      ),
      url,
    );
    const separator = path.includes('?') ? '&' : '?';
    history.push(
      `${path}${separator}${calendarTimeZone ? `calendarTimeZone=${encodeURIComponent(calendarTimeZone)}&` : ''}finishedExecutions=${trigger.finishedExecutions ?? 0}&expectedExecutions=${trigger.expectedExecutions ?? 0}${trigger.triggerId && trigger.triggerId !== trigger.id ? `&triggerRefId=${encodeURIComponent(trigger.triggerId)}` : ''}`,
      { shadow: true },
    );
    setActiveTab('timeline');
  };

  const progress = total > 0 && (
    <div className={styles.progress}>
      <Text type="secondary">
        <IntlMessages
          id={
            scheduleType === 'checkpoint_route'
              ? 'checkpoint.routes.realtime.markings.label'
              : 'dashboards.schedules.progressItem.executions.label'
          }
        />
        : {finished}/{total}
      </Text>
      <Progress percent={displayProgressPercentage((finished / total) * 100)} showInfo={false} />
    </div>
  );
  const timeline = (
    <BoxContainer
      className={styles.timelineContainer}
      content
      empty={!data?.rows.length}
      loading={loading}
    >
      <div className={styles.timeline}>
        {progress}
        {data?.rows.map(event => (
          <TimelineEvent event={event} key={event.id} />
        ))}
        {data?.nextCursor && (
          <div className={styles.loadMore}>
            <Button loading={loadingMore} onClick={loadMore}>
              <IntlMessages id="button.loadMore" />
            </Button>
          </div>
        )}
      </div>
    </BoxContainer>
  );

  return (
    <Drawer
      className={styles.drawer}
      closable={false}
      destroyOnClose
      footer={null}
      open={isVisible}
      title={null}
      onClose={onCancel}
    >
      <BoxContainer content bottomBorder fixed>
        <FilterContainer
          content={
            !triggerId && activeTab === 'timeline' ? (
              <KeyValueWidget
                label={<Title.Filter value={<IntlMessages id="general.form" />} />}
                value={
                  <FormSelect
                    allowClear
                    mode="multiple"
                    maxLength={10}
                    maxTagCount={5}
                    value={selectedEventTypes}
                    options={eventTypes}
                    onChange={value => setSelectedEventTypes(value as string[])}
                  />
                }
              />
            ) : null
          }
          title={<Title.Header value={data?.scheduleName ?? ''} />}
          goBack={onCancel}
        />
      </BoxContainer>
      {error ? (
        <ReportEventErrorState errorCode={errorCode} onRetry={refresh} />
      ) : !triggerId ? (
        <BoxContainer className={styles.body} content>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'events',
                label: <IntlMessages id="timeline.drawer.tab.events" />,
                children: (
                  <BoxContainer empty={!data?.triggerSummary.length} loading={loading}>
                    <ScheduleTriggersTable
                      triggers={data?.triggerSummary ?? []}
                      onSelectTrigger={openTrigger}
                    />
                  </BoxContainer>
                ),
              },
              {
                key: 'timeline',
                label: <IntlMessages id="timeline.drawer.tab.timeline" />,
                children: timeline,
              },
            ]}
          />
        </BoxContainer>
      ) : (
        timeline
      )}
    </Drawer>
  );
};

export default ReportEventDrawer;
